import { BreadcrumbType } from '@/types/BreadcrumbType';
import { BreadcrumbMatchResultType } from '@/types/BreadcrumbMatchResultType';
import { BreadcrumbRouteType } from '@/types/BreadcrumbRouteType';

export function getBreadcrumbsByPath(route: string): BreadcrumbType[] {
  route = route.split('#')[0];
  const match = findBestMatch(pathTemplates, route);

  if (!match) return [];
  const pathConfig = pathTemplates.find((pt) => pt.path === match.template);
  if (!pathConfig) return [];

  return pathConfig.getBreadcrumbs(match);
}

function findBestMatch(templates: BreadcrumbRouteType[], realPath: string) {
  if (realPath.endsWith('/')) realPath = realPath.slice(0, -1);
  const matches = templates
    .map((templateObj) => {
      const match = matchTemplate(templateObj.path, realPath);
      if (!match) return null;

      return {
        ...match,
        score: scoreTemplate(templateObj.path),
      };
    })
    .filter(Boolean) as (ReturnType<typeof matchTemplate> & { score: number })[];

  console.log('matches', matches);

  if (matches.length === 0) return null;

  return matches.sort((a, b) => b.score - a.score)[0];
}

function matchTemplate(template: string, realPath: string): BreadcrumbMatchResultType | null {
  const segments = template.split('/').filter((s) => s !== '');
  const keysList: string[] = [];
  const regexParts: string[] = [];

  segments.forEach((segment) => {
    if (segment.startsWith(':')) {
      const isOptional = segment.endsWith('?');
      const key = isOptional ? segment.slice(1, -1) : segment.slice(1);
      keysList.push(key);
      if (isOptional) {
        regexParts.push('(?:/([^/]+))?');
      } else {
        regexParts.push('/([^/]+)');
      }
    } else {
      regexParts.push('/' + segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    }
  });

  if (realPath.includes('?')) realPath = realPath.slice(0, realPath.indexOf('?'));

  const regexStr = `^${regexParts.join('')}/?$`;
  const regex = new RegExp(regexStr);
  const match = realPath.match(regex);

  if (!match) return null;

  const values = match.slice(1);

  const params: Record<string, string> = {};
  keysList.forEach((key, i) => {
    if (values[i] !== undefined) {
      params[key] = values[i];
    }
  });

  return { template, params };
}

function scoreTemplate(template: string): number {
  return template.split('/').reduce((score, segment) => {
    if (!segment) return score;

    if (segment === '*') return score + 1;
    if (segment.startsWith(':')) return score + 2;
    return score + 3; // statický segment
  }, 0);
}

const pathTemplates: BreadcrumbRouteType[] = [
  {
    path: '/dashboard/:view?',
    getBreadcrumbs: (match) => [
      {
        title: 'dashboard.default',
        href: `/dashboard/${match.params.view || ''}`,
        disabled: true,
      },
      {
        title: `dashboard.${match.params.view || 'default'}.title`,
        href: `/dashboard/${match.params.view || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/dashboard/assets/managerial-report',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'dashboard.assets.managerialReport',
        href: '/dashboard/assets/managerial-report',
        disabled: true,
      },
    ],
  },
  {
    path: '/dashboard/assets/assetGroupStatistics/:agUid/:rc?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'dashboard.assets.assetGroupStatistics',
        href: `/dashboard/assets/assetGroupStatistics/${match.params.agUid}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/dashboard/measures/measuresGroupStatistics/:mgUid',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'dashboard.measures.measuresGroupStatistics',
        href: `/dashboard/measures/measuresGroupStatistics/${match.params.mgUid}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/dashboard/measures/MeasureGroupWithImplementationsStatistics/:mgUid',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'dashboard.measures.MeasureGroupWithImplementationsStatistics',
        href: `/dashboard/measures/MeasureGroupWithImplementationsStatistics/${match.params.mgUid}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/groupable/:groupType/groups/',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: `groupable.groupTypes.${match.params.groupType}`,
        href: `/groupable/${match.params.groupType}/groups`,
        disabled: true,
      },
    ],
  },
  {
    path: '/groupable/:groupType/groups/list/:groupIdentifier',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: `groupable.groupTypes.${match.params.groupType}`,
        href: `/groupable/${match.params.groupType}/groups`,
        disabled: false,
      },
      {
        title: 'groupable.group.breadcrumbTitle',
        href: `/groupable/${match.params.groupType}/groups/list/${match.params.groupIdentifier}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/groupable/:groupType/groups/:action/:groupIdentifier?',
    getBreadcrumbs: (match) => {
      const breadcrumbs = [
        { title: 'dashboard.title', href: '/dashboard', disabled: false },
        {
          title: `groupable.groupTypes.${match.params.groupType}`,
          href: `/groupable/${match.params.groupType}/groups`,
          disabled: false,
        },
      ];

      if (match.params.action === 'edit') {
        breadcrumbs.push(
          {
            title: 'groupable.group.breadcrumbTitle',
            href: `/groupable/${match.params.groupType}/groups/list/${match.params.groupIdentifier}`,
            disabled: false,
          },
          {
            title: `groupable.group.form.edit.breadcrumbTitle`,
            href: `/groupable/${match.params.groupType}/groups/${match.params.action}/${match.params.groupIdentifier || ''}`,
            disabled: true,
          }
        );
      } else {
        breadcrumbs.push({
          title: `groupable.group.form.add.title`,
          href: `/groupable/${match.params.groupType}/groups/${match.params.action}/${match.params.groupIdentifier || ''}`,
          disabled: true,
        });
      }

      return breadcrumbs;
    },
  },
  {
    path: '/groupable/:groupType/groups/:groupIdentifier/item/:action/:itemIdentifier?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: `groupable.groupTypes.${match.params.groupType}`,
        href: `/groupable/${match.params.groupType}/groups`,
        disabled: false,
      },
      {
        title: 'groupable.group.breadcrumbTitle',
        href: `/groupable/${match.params.groupType}/groups/list/${match.params.groupIdentifier}`,
        disabled: false,
      },
      {
        title:
          match.params.action === 'edit'
            ? `groupable.item.form.edit.${match.params.groupType}.title`
            : `groupable.item.form.add.${match.params.groupType}.title`,
        href: `/groupable/${match.params.groupType}/groups/${match.params.groupIdentifier}/item/${match.params.action}/${match.params.itemIdentifier || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/groupable/:groupType/groups/:groupIdentifier/assetclone/:itemIdentifier',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: `groupable.groupTypes.${match.params.groupType}`,
        href: `/groupable/${match.params.groupType}/groups`,
        disabled: false,
      },
      {
        title: 'groupable.group.breadcrumbTitle',
        href: `/groupable/${match.params.groupType}/groups/list/${match.params.groupIdentifier}`,
        disabled: false,
      },
      {
        title: 'groupable.item.asset.clone.title',
        href: `/groupable/${match.params.groupType}/groups/${match.params.groupIdentifier}/assetclone/${match.params.itemIdentifier}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/groupable/:groupType/groups/:groupIdentifier/implementation/:measureIdentifier/:action/:itemIdentifier?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: `groupable.groupTypes.${match.params.groupType}`,
        href: `/groupable/${match.params.groupType}/groups`,
        disabled: false,
      },
      {
        title: 'groupable.group.breadcrumbTitle',
        href: `/groupable/${match.params.groupType}/groups/list/${match.params.groupIdentifier}`,
        disabled: false,
      },
      {
        title: match.params.action === 'edit' ? 'implementation.edit' : 'implementation.add',
        href: `/groupable/${match.params.groupType}/groups/${match.params.groupIdentifier}/implementation/${match.params.measureIdentifier}/${match.params.action}/${match.params.itemIdentifier || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/catalogue/impacts',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'catalogue.impacts.title', href: '/catalogue/impacts', disabled: true },
    ],
  },
  {
    path: '/catalogue/impacts/:action/:impactId?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'catalogue.impacts.title', href: '/catalogue/impacts', disabled: false },
      {
        title: match.params.action === 'edit' ? 'catalogue.impacts.edit' : 'catalogue.impacts.add',
        href: `/catalogue/impacts/${match.params.action}/${match.params.impactId || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/catalogue/threat-sources',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'catalogue.threatSources.title',
        href: '/catalogue/threat-sources',
        disabled: true,
      },
    ],
  },
  {
    path: '/catalogue/threat-sources/:action/:tsId?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'catalogue.threatSources.title',
        href: '/catalogue/threat-sources',
        disabled: false,
      },
      {
        title:
          match.params.action === 'edit'
            ? 'catalogue.threatSources.edit'
            : 'catalogue.threatSources.add',
        href: `/catalogue/threat-sources/${match.params.action}/${match.params.tsId || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/catalogue/threat-source-types',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'catalogue.threatSourceTypes.title',
        href: '/catalogue/threat-source-types',
        disabled: true,
      },
    ],
  },
  {
    path: '/catalogue/threat-source-types/:action/:tstId?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'catalogue.threatSourceTypes.title',
        href: '/catalogue/threat-source-types',
        disabled: false,
      },
      {
        title:
          match.params.action === 'edit'
            ? 'catalogue.threatSourceTypes.edit'
            : 'catalogue.threatSourceTypes.add',
        href: `/catalogue/threat-source-types/${match.params.action}/${match.params.tstId || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/catalogue/imp-exp',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'catalogue.imp-exp.title', href: '/catalogue/imp-exp', disabled: true },
    ],
  },
  {
    path: '/catalogue/imp-exp/help',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'catalogue.imp-exp.title', href: '/catalogue/imp-exp', disabled: false },
      { title: 'catalogue.imp-exp.help.title', href: '/catalogue/imp-exp/help', disabled: true },
    ],
  },
  {
    path: '/risk-analyse/risk-acceptance-decisions',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'riskAcceptanceDecisions.mainTitle',
        href: '/risk-analyse/risk-acceptance-decisions',
        disabled: true,
      },
    ],
  },
  {
    path: '/risk-analyse/risk-acceptance-decisions/:action/:identifier?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'riskAcceptanceDecisions.mainTitle',
        href: '/risk-analyse/risk-acceptance-decisions',
        disabled: false,
      },
      {
        title:
          match.params.action === 'edit'
            ? 'riskAcceptanceDecisions.edit'
            : 'riskAcceptanceDecisions.add',
        href: `/risk-analyse/risk-acceptance-decisions/${match.params.action}/${match.params.identifier || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/risk-analyse/risks/:assetId?/:avId?',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'risks.mainTitle', href: '/risk-analyse/risks', disabled: true },
    ],
  },
  {
    path: '/risk-analyse/risk/:assetId/:avId/:tgId/:tId/:action/:riskId?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'risks.mainTitle', href: '/risk-analyse/risks', disabled: false },
      {
        title: match.params.action === 'edit' ? 'risks.risk.editRisk' : 'risks.risk.createNewRisk',
        href: `/risk-analyse/risk/${match.params.assetId}/${match.params.avId}/${match.params.tgId}/${match.params.tId}/${match.params.action}/${match.params.riskId || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/risk-analyse/risk/:assetId/:avId/:tgId/:tId/:action/:riskId?/measure/:measureIdentifier/:action/:itemIdentifier?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'risks.mainTitle', href: '/risk-analyse/risks', disabled: false },
      {
        title: match.params.action === 'edit' ? 'risks.risk.editRisk' : 'risks.risk.createNewRisk',
        href: `/risk-analyse/risk/${match.params.assetId}/${match.params.avId}/${match.params.tgId}/${match.params.tId}/${match.params.action}/${match.params.riskId || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/report/:type/:reportBy?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: `reports.${match.params.type}.title`,
        href: `/report/${match.params.type}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/users',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'users.title', href: '/users', disabled: true },
    ],
  },
  {
    path: '/users/:action/:userId?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'users.title', href: '/users', disabled: false },
      {
        title: match.params.action === 'edit' ? 'users.form.edit.title' : 'users.form.add.title',
        href: `/users/${match.params.action}/${match.params.userId || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/confidentiality-levels',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'confidentialityLevels.mainTitle', href: '/confidentiality-levels', disabled: true },
    ],
  },
  {
    path: '/confidentiality-levels/:action/:confidentialityLevelId?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      {
        title: 'confidentialityLevels.mainTitle',
        href: '/confidentiality-levels',
        disabled: false,
      },
      {
        title:
          match.params.action === 'edit'
            ? 'confidentialityLevels.edit'
            : 'confidentialityLevels.add',
        href: `/confidentiality-levels/${match.params.action}/${match.params.confidentialityLevelId || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/document-references',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'documentReferences.mainTitle', href: '/document-references', disabled: true },
    ],
  },
  {
    path: '/document-references/:action/:documentReferenceId?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'documentReferences.mainTitle', href: '/document-references', disabled: false },
      {
        title:
          match.params.action === 'edit' ? 'documentReferences.edit' : 'documentReferences.add',
        href: `/document-references/${match.params.action}/${match.params.documentReferenceId || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/licences',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'licences.title', href: '/licences', disabled: true },
    ],
  },
  {
    path: '/licences/:action',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'licences.title', href: '/licences', disabled: false },
      {
        title: `licences.${match.params.action}`,
        href: `/licences/${match.params.action}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/settings',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'settings.title', href: '/settings', disabled: true },
    ],
  },
  {
    path: '/settings/tags',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'settings.title', href: '/settings', disabled: false },
      { title: 'settings.tags.title', href: '/settings/tags', disabled: true },
    ],
  },
  {
    path: '/settings/tags/:action/:tagIdentifier?',
    getBreadcrumbs: (match) => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'settings.title', href: '/settings', disabled: false },
      { title: 'settings.tags.title', href: '/settings/tags', disabled: false },
      {
        title: match.params.action === 'edit' ? 'settings.tags.edit' : 'settings.tags.add',
        href: `/settings/tags/${match.params.action}/${match.params.tagIdentifier || ''}`,
        disabled: true,
      },
    ],
  },
  {
    path: '/risk-analyse/manage',
    getBreadcrumbs: () => [
      { title: 'dashboard.title', href: '/dashboard', disabled: false },
      { title: 'risks.manage.mainTitle', href: '/risk-analyse/manage', disabled: true },
    ],
  },
];
